import { z } from "zod";
import { MobileResources } from "../../../core/resource/MobileResourceProvider";
import { formatMessage } from "../../../utils";
import {
  hasExtraWhiteSpace,
  isAlphaNumeric,
  isEmail,
  isName,
  isNumeric,
  isPhone,
  maxLength,
  minLength,
} from "../../../utils/validation";
import { AddUpdateEmployeeFormValues, AddUpdateEmployeeSchema } from "./types";

const MIN_PASSWORD_LENGTH = 6;
const MAX_PASSWORD_LENGTH = 9;

export const initialValues: AddUpdateEmployeeFormValues = {
  fullname: "",
  email: "",
  phone: {
    prefix: "",
    phone: "",
  },
  role: "",
  policy: "",
  division: null,
  birthdate: null,
  nationality: null,
  identityCardNumber: null,
  passportNumber: null,
  passportExpirationDate: null,
  passportCountryOfIssuance: null,
};

export function createFormSchema(
  cr: MobileResources["content"]["CorporateManagementEmployeeList"],
  cre: MobileResources["content"]["CorporateGeneralErrorMessage"]
): z.ZodSchema<AddUpdateEmployeeFormValues> {
  const phoneNumberRequired = formatMessage(cr.phoneMinimumLengthError, {
    num: 7,
  });
  return AddUpdateEmployeeSchema.extend({
    // Fullname validation
    fullname: z
      .string({ required_error: cr.fullnameInputRequired })
      .min(1, { message: cr.fullnameInputRequired })
      .superRefine((val, ctx) => {
        if (hasExtraWhiteSpace(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.inputExtraSpace,
          });
        }
        if (!isName(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.fullnameInputInvalid,
          });
        }
      }),

    // Email validation
    email: z
      .string({ required_error: cr.emailInputRequired })
      .min(1, { message: cr.emailInputRequired })
      .superRefine((val, ctx) => {
        if (!isEmail(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.emailInputInvalid,
          });
        }
      }),

    // Phone validation
    phone: z
      .object({
        prefix: z.string(),
        phone: z.string(),
      })
      .nullable()
      .superRefine((val, ctx) => {
        if (!val) return;
        const { prefix, phone } = val;
        if ((prefix && !phone) || (phone && !prefix)) {
          ctx.addIssue({
            path: ["phone"],
            code: z.ZodIssueCode.custom,
            message: phoneNumberRequired,
          });
        }
        if (phone) {
          if (!isPhone(phone)) {
            ctx.addIssue({
              path: ["phone"],
              code: z.ZodIssueCode.custom,
              message: cr.phoneNumericError,
            });
          }
          if (!minLength(phone, 7)) {
            ctx.addIssue({
              path: ["phone"],
              code: z.ZodIssueCode.custom,
              message: phoneNumberRequired,
            });
          }
          if (!maxLength(phone, 15)) {
            ctx.addIssue({
              path: ["phone"],
              code: z.ZodIssueCode.custom,
              message: formatMessage(cr.phoneMaximumLengthError, { num: 15 }),
            });
          }
        }
      }),
    role: z
      .string({ required_error: cr.userAccessRequired })
      .min(1, { message: cr.userAccessRequired }),
    policy: z
      .string({ required_error: cr.travelPolicyRequired })
      .min(1, { message: cr.travelPolicyRequired }),
    division: z.string().nullable(),
    birthdate: z
      .date({ invalid_type_error: cre.dateInputInvalidFormat })
      .nullable()
      .superRefine((val, ctx) => {
        if (!val) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (val > today) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cre.dateInputInvalidFormat,
          });
        }
        const twelveYearsAgo = new Date();
        twelveYearsAgo.setFullYear(today.getFullYear() - 12);
        if (val > twelveYearsAgo) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.birthDateMinimumAgeError,
          });
        }
      }),
    nationality: z.string().nullable(),
    identityCardNumber: z
      .string()
      .nullable()
      .superRefine((val, ctx) => {
        if (!val) return;
        if (!isNumeric(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.inputNumericError,
          });
        }
      }),
    passportNumber: z
      .string()
      .nullable()
      .superRefine((val, ctx) => {
        if (!val) return;

        if (!isAlphaNumeric(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.inputAlphaNumericError,
          });
        }
        if (!minLength(val, MIN_PASSWORD_LENGTH)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(cr.passportNumberMinimumLengthError, {
              num: MIN_PASSWORD_LENGTH,
            }),
          });
        }
        if (!maxLength(val, MAX_PASSWORD_LENGTH)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: formatMessage(cr.passportNumberMaximumLengthError, {
              num: MAX_PASSWORD_LENGTH,
            }),
          });
        }
      }),
    passportExpirationDate: z
      .date({ invalid_type_error: cre.dateInputInvalidFormat })
      .nullable(),
    passportCountryOfIssuance: z.string().nullable(),
  });
}
